import store from '@/store';
import {
  CountryCodes,
  Currencies,
  CurrencyCode,
  CurrencySymbolCode,
  CurrencySymbols,
  LocaleCode,
  Locales,
} from '@/Models/Localization';
import { Plan } from '@/classes/stripe';

type AllowedCurrenciesType = Currencies.EUR | Currencies.USD;
type AllowedCountryType = CountryCodes.DE;

export default class Currency {
  private readonly currencyToLocale = {
    [Currencies.EUR]: Locales.de_DE,
    [Currencies.USD]: Locales.en_US,
    [Currencies.GBP]: Locales.en_GB,
    [Currencies.BRL]: Locales.pt_BR,
  };

  private readonly countryToLocale = {
    [CountryCodes.DE]: Locales.de_DE,
  };

  constructor(public price: number = 0) {}

  currency: CurrencyCode = Currencies.EUR;
  symbol: CurrencySymbolCode = CurrencySymbols.EUR;
  locale: LocaleCode = Locales.de_DE;
  minimumFractionDigits: number = 2;
  useGrouping: boolean = false;

  priceToInt(price: string | number): number {
    if (!price) return 0;
    if (typeof price === 'string') {
      return parseFloat(price);
    }
    // Always show two decimals
    return Math.round(price * 100) / 100;
  }

  getLocaleFromCurrency(currency: string): string {
    return this.currencyToLocale[currency.toLocaleLowerCase() as AllowedCurrenciesType] || 'de-DE';
  }

  getLocaleFromCountry(country: string): string {
    return this.countryToLocale[country.toLocaleLowerCase() as AllowedCountryType] || 'de-DE';
  }

  // Core function to format price with Intl.NumberFormat
  private format({
    options = {},
    overrideCurrency = '',
    useGrouping = false,
  }: {
    options?: Record<string, unknown>;
    overrideCurrency?: string;
    useGrouping?: boolean;
  }): Intl.NumberFormat {
    const { currency } = store.state.auth?.team || {};
    const { country_iso2 } = store.state.location;

    let locale: string = Locales.de_DE;
    let uppercaseCurrency = currency.toUpperCase();

    if (overrideCurrency) {
      uppercaseCurrency = overrideCurrency.toUpperCase();
    }

    // If user has usd inside database detect locale based on currency value
    if (uppercaseCurrency === Currencies.USD.toUpperCase()) {
      locale = this.getLocaleFromCurrency(uppercaseCurrency);
    } else if (country_iso2) {
      locale = this.getLocaleFromCountry(country_iso2);
    }

    return new Intl.NumberFormat(locale, {
      ...options,
      minimumFractionDigits: this.minimumFractionDigits,
      currency: uppercaseCurrency,
      useGrouping,
    });
  }

  // Display price without the symbol (Example: 460.00)
  priceAsValue({
    price = this.price,
    overrideCurrency = '',
    useGrouping = false,
  }: {
    price: number | string;
    overrideCurrency?: string;
    useGrouping?: boolean;
  }): string {
    return this.format({ overrideCurrency, useGrouping }).format(this.priceToInt(price));
  }

  // Display full price with symbol together (Example: $460.00)
  displayPrice({
    price = this.price,
    overrideCurrency = '',
    useGrouping = false,
  }: {
    price: number | string;
    overrideCurrency?: string;
    useGrouping?: boolean;
  }): string {
    return this.format({ options: { style: 'currency' }, overrideCurrency, useGrouping }).format(
      this.priceToInt(price)
    );
  }

  // Returns only symbol
  displaySymbol({ overrideCurrency = '' }: { overrideCurrency?: string }): string {
    return (
      this.format({ options: { style: 'currency' }, overrideCurrency })
        .formatToParts(this.priceToInt(this.price))
        .find((obj) => obj.type === 'currency')?.value || CurrencySymbols.EUR
    );
  }

  // Returns formatted monthly price (Example: 38.33)
  displayPriceMonthly({
    plan,
    selectedInterval = '',
    overrideCurrency = '',
  }: {
    plan: Plan;
    selectedInterval: string;
    overrideCurrency?: string;
  }): string {
    if (!plan || !selectedInterval) return '';
    let formattedPrice = this.priceToInt(this.price);

    // Yearly Plan and selected monthly display
    if (plan.interval === 'year' && selectedInterval === 'month') {
      formattedPrice /= 12;
    }

    // Monthly Plan and selected yearly display
    if (!(plan.interval === 'year') && selectedInterval === 'year') {
      formattedPrice *= 12;
    }

    return this.priceAsValue({ price: formattedPrice, overrideCurrency });
  }

  toJSON(): number {
    return this.price;
  }

  toString(): string {
    return this.displayPrice({ price: this.price });
  }
}
